import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/config";
import {
  getRanking,
  getTestRanking,
  getSpeedStats,
  getStudentStats,
  getUsersByField,
} from "../../../services/moodle/ranking";
import { createLogger } from "../../../utils/debug";
import PublicIcon from "@mui/icons-material/Public";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarsIcon from "@mui/icons-material/Stars";
import { HelpTooltip } from "../../../components/HelpTooltip";
import { getActivityLog } from "../../../services/moodle/activity";
import "./index.css";
import { ScoringRules } from "./ScoringRules";

const log = createLogger("GlobalRanking");

/**
 * GlobalRanking — "Premio del mes" (Points) and "Ranking Test" (GPA >= 10 quizzes)
 */

const MEDALS = { 1: "gold", 2: "silver", 3: "bronze" };

const RankingTitle = ({ title, subtitle }) => (
  <h3 className="gr-title-with-help font-montserrat">
    {title}
    <HelpTooltip
      text={subtitle}
      ariaLabel={`Más información sobre ${title}`}
      className="gr-title-help"
    />
  </h3>
);

const PointsBadge = ({ points, bonus }) => (
  <span
    className={
      "gr-points-badge shrink-0" +
      (points === 0 ? " is-zero" : "") +
      (bonus ? " is-bonus" : "")
    }
  >
    {points === 0 ? "0 pts" : `+${points} ${points === 1 ? "pt" : "pts"}`}
  </span>
);

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const formatRelativo = (timestamp) => {
  if (!timestamp) return "";
  const diffSeg = Math.floor(Date.now() / 1000) - Number(timestamp);
  if (diffSeg < 60) return "recién";
  if (diffSeg < 3600) return `hace ${Math.floor(diffSeg / 60)} min`;
  if (diffSeg < 86400) return `hace ${Math.floor(diffSeg / 3600)} h`;
  const dias = Math.floor(diffSeg / 86400);
  if (dias < 7) return dias === 1 ? "hace 1 día" : `hace ${dias} días`;
  const d = new Date(Number(timestamp) * 1000);
  return `${d.getDate()} ${MESES[d.getMonth()]}`;
};

const UserPointsLog = ({ token, baseUrl, currentUserId, userPoints }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getActivityLog({
      token,
      baseUrl,
      userid: currentUserId || 0,
      limit: 50,
      offset: 0,
    })
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        // Only keep events where points were awarded
        const pointsOnly = list.filter((item) => Number(item.points) > 0);
        setEntries(pointsOnly);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error("error fetching user points log", err);
          setError("No se pudo cargar el historial de puntos.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, baseUrl, currentUserId]);

  const displayTotal =
    userPoints !== null && userPoints !== undefined
      ? userPoints
      : entries.reduce((acc, curr) => acc + (Number(curr.points) || 0), 0);

  return (
    <>
      <div className="gr-card-head justify-between">
        <div className="flex items-center gap-3">
          <span
            className="gr-trophy text-md-primary-container flex items-center"
            role="img"
            aria-label="puntos obtenidos"
          >
            <StarsIcon sx={{ fontSize: 32 }} />
          </span>
          <div>
            <RankingTitle
              title="Tus puntos obtenidos"
              subtitle="Historial de actividades que sumaron puntos"
            />
          </div>
        </div>
        <span
          className="gr-points-badge is-bonus"
          title="Total de puntos acumulados"
        >
          {displayTotal} {displayTotal === 1 ? "pt" : "pts"}
        </span>
      </div>

      {loading && <p className="gr-state">Cargando tus puntos…</p>}
      {!loading && error && <p className="gr-state gr-state-error">{error}</p>}
      {!loading && !error && entries.length === 0 && (
        <p className="gr-state" style={{ fontSize: "max(1rem, 16px)", fontStyle: "italic" }}>
          Aún no tienes puntos registrados este mes. ¡Participa en las actividades para sumar!
        </p>
      )}

      {!loading && !error && entries.length > 0 && (
        <ul className="gr-rule-list gr-activity-list">
          {entries.map((entry) => {
            const pts = Number(entry.points) || 0;
            return (
              <li key={entry.id} className="gr-rule gr-activity-row">
                <div className="gr-rule-main">
                  <div className="gr-activity-info min-w-0 flex-1">
                    <span className="gr-rule-label font-semibold line-clamp-2">
                      {entry.description}
                    </span>
                    <span className="gr-activity-time text-xs">
                      {formatRelativo(entry.timecreated)}
                    </span>
                  </div>
                  <PointsBadge points={pts} bonus={pts >= 10} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

const LeftColumnCard = ({
  rows,
  currentUserId,
  loading,
  error,
  onSeeAll,
  token,
  baseUrl,
}) => {
  const myRow = rows.find((r) => String(r.userid) === String(currentUserId));

  return (
    <div className="gr-card gr-left-card">
      {/* Top Half: Leaderboard Top 5 */}
      <div className="gr-split-top">
        <div className="gr-card-head justify-between">
          <div className="flex items-center gap-3">
            <span className="gr-trophy text-md-primary-container flex items-center" role="img" aria-label="premio">
              <WorkspacePremiumIcon sx={{ fontSize: 32 }} />
            </span>
            <div>
              <RankingTitle
                title="Premio del mes"
                subtitle="Ranking global de puntos acumulados"
              />
            </div>
          </div>
          {!loading && !error && rows.length > 0 && (
            <button
              type="button"
              onClick={onSeeAll}
              className="gr-see-all-btn group"
            >
              <span>Ver todos</span>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>

        {loading && <p className="gr-state">Cargando ranking…</p>}
        {!loading && error && <p className="gr-state gr-state-error">{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="gr-state">Aún no hay puntos registrados.</p>
        )}

        {!loading && !error && rows.length > 0 && (
          <ul className="gr-rule-list">
            {rows.slice(0, 5).map((row) => {
              const medal = MEDALS[row.rank];
              const isMe = String(row.userid) === String(currentUserId);
              return (
                <li
                  key={row.userid}
                  className={
                    "gr-rule gr-row" +
                    (medal ? ` medal-${medal}` : "") +
                    (isMe ? " is-me" : "")
                  }
                >
                  <div className="gr-rule-main">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <span className="gr-rank shrink-0">{row.rank}</span>
                      <span className="gr-rule-label font-semibold truncate">{row.name}</span>
                      {isMe && <span className="gr-me-tag shrink-0">Tú</span>}
                    </div>
                    <span
                      className={
                        "gr-points-badge shrink-0" +
                        (row.rank === 1 ? " is-bonus" : "") +
                        (row.points === 0 ? " is-zero" : "")
                      }
                    >
                      {row.points} {row.points === 1 ? "pt" : "pts"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Horizontal divider dividing the card across the middle */}
      <div className="gr-split-divider" />

      {/* Bottom Half: User points log */}
      <div className="gr-split-bottom">
        <UserPointsLog
          token={token}
          baseUrl={baseUrl}
          currentUserId={currentUserId}
          userPoints={myRow ? myRow.points : null}
        />
      </div>
    </div>
  );
};

const FullRankingView = ({ rows, currentUserId }) => (
  <div className="gr-card gr-all-ranking">
    <div className="gr-card-head justify-between">
      <div className="flex items-center gap-3">
        <span className="gr-trophy text-md-primary-container flex items-center" role="img" aria-label="premio">
          <WorkspacePremiumIcon sx={{ fontSize: 32 }} />
        </span>
        <div>
          <RankingTitle
            title="Ranking histórico completo"
            subtitle={`Clasificación general de alumnos (${rows.length} ${rows.length === 1 ? "alumno" : "alumnos"})`}
          />
        </div>
      </div>
    </div>

    <ul className="gr-rule-list gr-all-list">
      {rows.map((row) => {
        const medal = MEDALS[row.rank];
        const isMe = String(row.userid) === String(currentUserId);
        return (
          <li
            key={row.userid}
            className={
              "gr-rule gr-row" +
              (medal ? ` medal-${medal}` : "") +
              (isMe ? " is-me" : "")
            }
          >
            <div className="gr-rule-main">
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <span className="gr-rank shrink-0">{row.rank}</span>
                <span className="gr-rule-label font-semibold truncate">{row.name}</span>
                {isMe && <span className="gr-me-tag shrink-0">Tú</span>}
              </div>
              <span
                className={
                  "gr-points-badge shrink-0" +
                  (row.rank === 1 ? " is-bonus" : "") +
                  (row.points === 0 ? " is-zero" : "")
                }
              >
                {row.points} {row.points === 1 ? "pt" : "pts"}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

const UserTestsLog = ({
  token,
  baseUrl,
  currentUserId,
  testsDone,
  isQualified,
  myRow,
  myGpa,
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getActivityLog({
      token,
      baseUrl,
      userid: currentUserId || 0,
      limit: 50,
      offset: 0,
    })
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        const quizOnly = list.filter((item) => item.type === "quiz_completed");
        setEntries(quizOnly);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error("error fetching user tests log", err);
          setError("No se pudo cargar el historial de cuestionarios.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, baseUrl, currentUserId]);

  const currentGrade = myRow ? myRow.grade : myGpa || 0;

  return (
    <>
      <div className="gr-card-head justify-between font-montserrat">
        <div className="flex items-center gap-3">
          <span
            className="gr-trophy text-md-primary-container flex items-center"
            role="img"
            aria-label="tests realizados"
          >
            <FactCheckIcon sx={{ fontSize: 32 }} />
          </span>
          <div>
            <RankingTitle
              title="Tus tests realizados"
              subtitle={isQualified
                ? "Historial de cuestionarios completados"
                : `${testsDone} de 10 completados para clasificar`}
            />
          </div>
        </div>
        {isQualified ? (
          <span
            className="gr-points-badge is-bonus shrink-0 font-montserrat"
            title={`Nota media: ${Number(currentGrade).toFixed(2).replace(".", ",")}`}
          >
            {testsDone} {testsDone === 1 ? "test" : "tests"} • Media{" "}
            {Number(currentGrade).toFixed(2).replace(".", ",")}
          </span>
        ) : (
          <span
            className="gr-points-badge shrink-0 font-montserrat"
            title="Progreso hacia los 10 cuestionarios requeridos"
          >
            {testsDone} / 10 tests
          </span>
        )}
      </div>

      {loading && <p className="gr-state font-montserrat">Cargando tus tests…</p>}
      {!loading && error && (
        <p className="gr-state gr-state-error font-montserrat">{error}</p>
      )}
      {!loading && !error && entries.length === 0 && (
        <p
          className="gr-state font-montserrat"
          style={{ fontSize: "max(1rem, 16px)", fontStyle: "italic" }}
        >
          {testsDone > 0
            ? `Tienes ${testsDone} ${testsDone === 1 ? "cuestionario completado" : "cuestionarios completados"}. Los próximos aparecerán detallados aquí.`
            : "Aún no tienes cuestionarios registrados en el historial. ¡Realiza un test para comenzar!"}
        </p>
      )}

      {!loading && !error && entries.length > 0 && (
        <ul className="gr-rule-list gr-activity-list font-montserrat">
          {entries.map((entry) => (
            <li key={entry.id} className="gr-rule gr-activity-row font-montserrat">
              <div className="gr-rule-main font-montserrat">
                <div className="gr-activity-info min-w-0 flex-1 font-montserrat">
                  <span className="gr-rule-label font-semibold line-clamp-2 font-montserrat">
                    {entry.description}
                  </span>
                  <span className="gr-activity-time text-xs font-montserrat">
                    {formatRelativo(entry.timecreated)}
                  </span>
                </div>
                <span className="gr-test-pill shrink-0 font-montserrat">Test</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export const RankingTestCard = ({
  rows = [],
  currentUserId,
  loading,
  error,
  onSeeAll,
  myTestsDone = 0,
  myGpa = 0,
  token,
  baseUrl,
}) => {
  const myRow = rows.find((r) => String(r.userid) === String(currentUserId));
  const testsDone = myRow ? myRow.quizzescount : myTestsDone;
  const isQualified = testsDone >= 10;

  return (
    <div className="gr-card gr-left-card font-montserrat">
      {/* Top Half: Test Leaderboard Top 5 */}
      <div className="gr-split-top font-montserrat">
        <div className="gr-card-head justify-between font-montserrat">
          <div className="flex items-center gap-3">
            <span
              className="gr-trophy text-md-primary-container flex items-center"
              role="img"
              aria-label="ranking test"
            >
              <FactCheckIcon sx={{ fontSize: 32 }} />
            </span>
            <div>
              <RankingTitle
                title="Ranking Test"
                subtitle="Nota media con al menos 10 cuestionarios"
              />
            </div>
          </div>
          {!loading && !error && rows.length > 0 && (
            <button
              type="button"
              onClick={onSeeAll}
              className="gr-see-all-btn group font-montserrat"
            >
              <span>Ver todos</span>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>

        {loading && <p className="gr-state font-montserrat">Cargando ranking test…</p>}
        {!loading && error && <p className="gr-state gr-state-error font-montserrat">{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="gr-state font-montserrat">Aún no hay alumnos con al menos 10 cuestionarios realizados.</p>
        )}

        {!loading && !error && rows.length > 0 && (
          <ul className="gr-rule-list font-montserrat">
            {rows.slice(0, 5).map((row) => {
              const medal = MEDALS[row.rank];
              const isMe = String(row.userid) === String(currentUserId);
              return (
                <li
                  key={row.userid}
                  className={
                    "gr-rule gr-row font-montserrat" +
                    (medal ? ` medal-${medal}` : "") +
                    (isMe ? " is-me" : "")
                  }
                >
                  <div className="gr-rule-main font-montserrat">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1 font-montserrat">
                      <span className="gr-rank shrink-0 font-montserrat">{row.rank}</span>
                      <span className="gr-rule-label font-semibold truncate font-montserrat">{row.name}</span>
                      {isMe && <span className="gr-me-tag shrink-0 font-montserrat">Tú</span>}
                    </div>
                    <span
                      className={
                        "gr-points-badge gr-grade-badge shrink-0 font-montserrat" +
                        (row.rank === 1 ? " is-bonus" : "")
                      }
                      title={`Nota media: ${Number(row.grade).toFixed(2).replace(".", ",")} (${row.quizzescount} ${row.quizzescount === 1 ? "test" : "tests"})`}
                    >
                      {Number(row.grade).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Horizontal divider */}
      <div className="gr-split-divider" />

      {/* Bottom Half: Current user test history */}
      <div className="gr-split-bottom font-montserrat">
        <UserTestsLog
          token={token}
          baseUrl={baseUrl}
          currentUserId={currentUserId}
          testsDone={testsDone}
          isQualified={isQualified}
          myRow={myRow}
          myGpa={myGpa}
        />
      </div>
    </div>
  );
};

const FullTestRankingView = ({ rows, currentUserId }) => (
  <div className="gr-card gr-all-ranking font-montserrat">
    <div className="gr-card-head justify-between font-montserrat">
      <div className="flex items-center gap-3">
        <span
          className="gr-trophy text-md-primary-container flex items-center"
          role="img"
          aria-label="ranking test"
        >
          <FactCheckIcon sx={{ fontSize: 32 }} />
        </span>
        <div>
          <RankingTitle
            title="Ranking Test completo"
            subtitle={`Clasificación general por nota media (${rows.length} ${rows.length === 1 ? "alumno clasificado" : "alumnos clasificados"})`}
          />
        </div>
      </div>
    </div>

    <ul className="gr-rule-list gr-all-list font-montserrat">
      {rows.map((row) => {
        const medal = MEDALS[row.rank];
        const isMe = String(row.userid) === String(currentUserId);
        return (
          <li
            key={row.userid}
            className={
              "gr-rule gr-row font-montserrat" +
              (medal ? ` medal-${medal}` : "") +
              (isMe ? " is-me" : "")
            }
          >
            <div className="gr-rule-main font-montserrat">
              <div className="flex items-center gap-2.5 min-w-0 flex-1 font-montserrat">
                <span className="gr-rank shrink-0 font-montserrat">{row.rank}</span>
                <span className="gr-rule-label font-semibold truncate font-montserrat">{row.name}</span>
                {isMe && <span className="gr-me-tag shrink-0 font-montserrat">Tú</span>}
              </div>
                <span
                  className={
                    "gr-points-badge gr-grade-badge shrink-0 font-montserrat" +
                    (row.rank === 1 ? " is-bonus" : "")
                  }
                  title={`Nota media: ${Number(row.grade).toFixed(2).replace(".", ",")} (${row.quizzescount} ${row.quizzescount === 1 ? "test" : "tests"})`}
                >
                  {Number(row.grade).toFixed(2).replace(".", ",")}
                </span>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

export const GlobalRanking = () => {
  const { user } = useAuth();
  const { config } = useConfig();
  const token = user?.token || localStorage.getItem("token");
  const currentUserId = user?.id ? String(user.id) : null;
  const baseUrl = config?.baseURL;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [testRows, setTestRows] = useState([]);
  const [testLoading, setTestLoading] = useState(true);
  const [testError, setTestError] = useState(null);
  const [myTestsDone, setMyTestsDone] = useState(0);
  const [myGpa, setMyGpa] = useState(0);

  const [activeView, setActiveView] = useState("main"); // "main" | "all_points" | "all_tests"

  useEffect(() => {
    let cancelled = false;
    log.info("mount", { hasToken: !!token, currentUserId });
    if (!token) {
      log.warn("no active session — skipping ranking fetch");
      setLoading(false);
      setTestLoading(false);
      setError("No hay sesión activa.");
      return;
    }

    // 1. Fetch Points Ranking
    (async () => {
      try {
        setLoading(true);
        const { ranking = [] } = await getRanking({ token, baseUrl });
        const ids = ranking.map((r) => r.userid);
        const users = await getUsersByField({ token, ids, baseUrl });
        const byId = new Map(users.map((u) => [u.id, u.fullname]));

        const resolved = ranking.map((r) => ({
          rank: r.rank,
          userid: r.userid,
          points: r.points,
          name: byId.get(r.userid) || `Usuario ${r.userid}`,
        }));

        if (!cancelled) {
          setRows(resolved);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "No se pudo cargar el ranking.");
          log.error("ranking fetch failed", e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // 2. Fetch Ranking Test (min 10 quizzes)
    (async () => {
      try {
        setTestLoading(true);
        const { ranking = [] } = await getTestRanking({ token, baseUrl, minquizzes: 10 });
        const ids = ranking.map((r) => r.userid);
        const users = await getUsersByField({ token, ids, baseUrl });
        const byId = new Map(users.map((u) => [u.id, u.fullname]));

        const resolved = ranking.map((r) => ({
          rank: r.rank,
          userid: r.userid,
          grade: Number(r.grade),
          quizzescount: Number(r.quizzescount),
          name: byId.get(r.userid) || `Usuario ${r.userid}`,
        }));

        if (!cancelled) {
          setTestRows(resolved);
        }
      } catch (e) {
        if (!cancelled) {
          setTestError(e.message || "No se pudo cargar el Ranking Test.");
          log.warn("test ranking fetch error", e);
        }
      } finally {
        if (!cancelled) setTestLoading(false);
      }
    })();

    // 3. Fetch current user test stats for eligibility
    if (currentUserId) {
      getSpeedStats({ token, userid: currentUserId, baseUrl })
        .then((data) => {
          if (!cancelled && data) {
            setMyTestsDone(Number(data.my_tests_done) || 0);
          }
        })
        .catch(() => {});

      getStudentStats({ token, userid: currentUserId, baseUrl })
        .then((data) => {
          if (!cancelled && data) {
            setMyGpa(Number(data.gpa) || 0);
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      log.info("unmount / cleanup");
    };
  }, [token, currentUserId, baseUrl]);

  return (
    <section className="global-ranking font-montserrat">
      <div className="gr-header">
        {activeView !== "main" ? (
          <>
            <button
              onClick={() => setActiveView("main")}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors duration-150 mr-2 cursor-pointer font-montserrat shrink-0"
              style={{
                backgroundColor: "rgba(255,255,255,0.08)",
                color: "var(--progress-text-primary, #e2e1f4)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              Volver
            </button>
            <h2>
              <strong>Ranking global</strong> <span className="gr-sep">|</span>{" "}
              <span className="gr-header-subtitle" style={{ color: "#ecb236" }}>
                {activeView === "all_points" ? "Top histórico de puntos" : "Ranking Test completo"}
              </span>
            </h2>
          </>
        ) : (
          <>
            <span
              className="gr-header-icon text-md-primary-container flex items-center justify-center shrink-0"
              role="img"
              aria-label="ranking"
            >
              <PublicIcon sx={{ fontSize: 32 }} />
            </span>
            <h2>
              <strong>Ranking global</strong> <span className="gr-sep">|</span>{" "}
              <span className="gr-header-subtitle">Clasificaciones</span>
            </h2>
          </>
        )}
      </div>

      <div className="gr-panels-wrapper">
        <div className={`gr-panel gr-panel-main${activeView !== "main" ? " slide-out" : ""}`}>
          <div className="gr-grid">
            <LeftColumnCard
              rows={rows}
              currentUserId={currentUserId}
              loading={loading}
              error={error}
              onSeeAll={() => setActiveView("all_points")}
              token={token}
              baseUrl={baseUrl}
            />
            <RankingTestCard
              rows={testRows}
              currentUserId={currentUserId}
              loading={testLoading}
              error={testError}
              onSeeAll={() => setActiveView("all_tests")}
              myTestsDone={myTestsDone}
              myGpa={myGpa}
              token={token}
              baseUrl={baseUrl}
            />
          </div>
        </div>

        {activeView === "all_points" && (
          <div className="gr-panel gr-panel-all slide-in">
            <FullRankingView
              rows={rows}
              currentUserId={currentUserId}
              onBack={() => setActiveView("main")}
            />
          </div>
        )}

        {activeView === "all_tests" && (
          <div className="gr-panel gr-panel-all slide-in">
            <FullTestRankingView
              rows={testRows}
              currentUserId={currentUserId}
              onBack={() => setActiveView("main")}
            />
          </div>
        )}
      </div>
      <ScoringRules />
    </section>
  );
};

export default GlobalRanking;
